import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authMe, authLogout, clearSessionToken } from '../lib/api';

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  phone?: string | null;
  company_name?: string | null;
  btw_number?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  notes?: string | null;
}

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => void;
  isAdmin: boolean;
  isWholesaler: boolean;
  /** Whether a session restore is still in progress (avoids flash of "not logged in") */
  authLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // On mount: restore session via /api/auth/me if a token exists
  useEffect(() => {
    // V6 gsm-pass: zonder token geen /me-call — scheelt een 401 in de
    // console (en een request) voor elke anonieme bezoeker.
    if (!localStorage.getItem('gevel_session_token')) { setAuthLoading(false); return; }
    authMe()
      .then(apiUser => {
        if (apiUser) {
          setUserState(apiUser);
          // v3.2.77 — Filip's klacht: 'sluit de bovenste menu tegen de
          // bovenkant zonder opening boven'. Oorzaak: een stale
          // 'gvs-admin-token' in localStorage uit een eerdere
          // impersonation-sessie deed de Navbar denken dat Filip aan
          // het impersoneren was → 36px offset. Cleanup hier:
          //   • Als gvs-admin-token === huidige session token → we ZIJN
          //     de admin, geen impersonation actief → wis het.
          //   • Als de huidige user owner/superadmin is en er een token
          //     staat → ook stale (een echte impersonation zou een
          //     ander session token gebruiken) → wis het.
          try {
            const adminToken = localStorage.getItem('gvs-admin-token');
            const currentToken = localStorage.getItem('gevel_session_token');
            const isStale =
              adminToken &&
              (adminToken === currentToken
                || apiUser.role === 'owner'
                || apiUser.role === 'superadmin');
            if (isStale) {
              localStorage.removeItem('gvs-admin-token');
              localStorage.removeItem('gvs-admin-name');
              localStorage.removeItem('gvs-admin-role');
              // Body class kan blijven hangen uit een eerdere mount;
              // forceer cleanup zodat de 36px padding-top weg is.
              document.body.classList.remove('has-admin-banner');
            }
          } catch { /* ignore localStorage errors */ }
        }
      })
      .finally(() => setAuthLoading(false));
  }, []);

  const setUser = (u: User | null) => {
    setUserState(u);
  };

  const logout = async () => {
    await authLogout();
    clearSessionToken();
    setUserState(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      setUser,
      logout,
      isAdmin: user?.role === 'admin',
      isWholesaler: user?.role === 'wholesaler',
      authLoading,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
